import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
// Phase 7 sign-off: profiles identifier, csv_cell (HTML body), short_text.
import { MAX_LENGTHS } from '../../utils/inputValidation';

export default function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null); // full template object with html
  const [html, setHtml] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [varValues, setVarValues] = useState({});
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const loadList = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/templates');
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const selectTemplate = async (name) => {
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${name}`);
      if (res.ok) {
        const data = await res.json();
        setSelected(data);
        setHtml(data.html);
        setPreviewHtml('');
        const vars = {};
        (data.variables || []).forEach(v => { vars[v] = ''; });
        setVarValues(vars);
        setCreating(false);
      }
    } catch { showToast('error', 'Failed to load template'); }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${selected.name}`, {
        method: 'PUT',
        body: JSON.stringify({ html }),
      });
      if (res.ok) {
        showToast('success', 'Template saved');
        const data = await res.json();
        setSelected(data);
        loadList();
      } else {
        const data = await res.json();
        showToast('error', data.detail || 'Save failed');
      }
    } catch { showToast('error', 'Network error'); }
    setSaving(false);
  };

  const handlePreview = async () => {
    if (!selected) return;
    try {
      const res = await apiFetch(`/api/v1/admin/templates/${selected.name}/preview`, {
        method: 'POST',
        body: JSON.stringify({ variables: varValues, locale: 'ko' }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.html);
      }
    } catch { showToast('error', 'Preview failed'); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await apiFetch('/api/v1/admin/templates', {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), html: html || '<h1>New Template</h1>' }),
      });
      if (res.ok) {
        const data = await res.json();
        showToast('success', 'Template created');
        setCreating(false);
        setNewName('');
        loadList();
        setSelected(data);
        setHtml(data.html);
      } else {
        const data = await res.json();
        showToast('error', data.detail || 'Create failed');
      }
    } catch { showToast('error', 'Network error'); }
  };

  return (
    <div>
      {toast && (
        <div className={`em-toast em-toast-${toast.type}`}>
          {toast.msg}
          <button onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <div className="em-toolbar">
        <select
          value={selected?.name || ''}
          onChange={e => e.target.value && selectTemplate(e.target.value)}
          style={{ minWidth: 200 }}
        >
          <option value="">Select a template...</option>
          {templates.map(t => (
            <option key={t.name} value={t.name}>{t.name} ({t.filename})</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <button className="em-btn em-btn-primary" onClick={() => { setCreating(true); setSelected(null); setHtml('<h1>New Template</h1>'); setPreviewHtml(''); }}>
          + New Template
        </button>
      </div>

      {creating && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input
            type="text"
            maxLength={MAX_LENGTHS.identifier}
            placeholder="Template name (alphanumeric/underscores)"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            style={{ flex: 1, padding: '6px 10px' }}
          />
          <button className="em-btn em-btn-primary" onClick={handleCreate}>Create</button>
          <button className="em-btn" onClick={() => setCreating(false)}>Cancel</button>
        </div>
      )}

      {(selected || creating) && (
        <div className="em-editor-layout">
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>{creating ? 'New Template' : selected?.name}</strong>
              {selected && !creating && (
                <button className="em-btn em-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
            <div className="em-editor-pane">
              <textarea
                value={html}
                onChange={e => setHtml(e.target.value)}
                maxLength={MAX_LENGTHS.csv_cell}
                spellCheck={false}
              />
            </div>

            {/* Variable inspector */}
            {selected && selected.variables && selected.variables.length > 0 && (
              <div className="em-var-inspector">
                <strong>Template Variables</strong>
                {selected.variables.map(v => (
                  <div key={v} style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                    <code style={{ minWidth: 120 }}>{`{{${v}}}`}</code>
                    <input
                      type="text"
                      maxLength={MAX_LENGTHS.short_text}
                      placeholder={`Value for ${v}`}
                      value={varValues[v] || ''}
                      onChange={e => setVarValues(prev => ({ ...prev, [v]: e.target.value }))}
                      style={{ flex: 1, padding: '4px 8px' }}
                    />
                  </div>
                ))}
                <button className="em-btn" style={{ marginTop: 8 }} onClick={handlePreview}>
                  Preview with variables
                </button>
              </div>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>Preview</strong>
              {selected && !previewHtml && (
                <button className="em-btn" onClick={handlePreview}>Render Preview</button>
              )}
            </div>
            <div className="em-preview-pane">
              {previewHtml ? (
                <iframe
                  title="Template Preview"
                  srcDoc={previewHtml}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  sandbox="allow-same-origin"
                />
              ) : (
                <div style={{ padding: 20, color: 'var(--text-secondary)', textAlign: 'center' }}>
                  Click "Render Preview" to see the rendered template
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!selected && !creating && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
          Select a template from the dropdown or create a new one
        </div>
      )}
    </div>
  );
}
